'use client';

import React from 'react';
import Link from 'next/link';
import { ShoppingBag, Truck, Building2, Phone, Sparkles, ArrowRight, MessageCircle } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

export default function FinalCTA() {
  const { setIsContactOpen } = useCommerce();

  return (
    <section
      id="final-cta"
      className="relative py-20 sm:py-24 lg:py-28 bg-[#0D0303] text-[#F7F4D6] overflow-hidden select-none border-t-2 border-b-4 border-[#E09F55]/60"
    >
      {/* 1. Deep Royal Ambient Gradients & Glows (Matching Loading Page Heritage Theme) */}
      <div className="absolute inset-0 bg-radial from-[#380604] via-[#160404] to-[#0A0202] opacity-95 pointer-events-none" />

      {/* 2. Traditional South Indian Kolam / Lattice Motif (Sacred geometry overlay) */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(#E09F55 1.5px, transparent 1.5px), radial-gradient(#F2BC6F 1px, transparent 1px)`,
          backgroundSize: '36px 36px',
          backgroundPosition: '0 0, 18px 18px',
        }}
      />

      {/* 3. Luminous Central & Lateral Radiant Ambient Auras */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[800px] h-[380px] sm:h-[480px] rounded-full bg-gradient-to-tr from-[#950E09]/30 via-[#E09F55]/20 to-[#6F0A06]/35 blur-[100px] pointer-events-none animate-pulse" />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#E09F55]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#950E09]/20 blur-3xl pointer-events-none" />

      {/* 4. Luxury Corner Flourishes */}
      <div className="absolute top-6 left-6 text-[#E09F55]/30 hidden sm:flex items-center space-x-2 text-xs font-serif tracking-widest uppercase">
        <span>✦</span>
        <span>CHETTINAD HERITAGE</span>
      </div>
      <div className="absolute top-6 right-6 text-[#E09F55]/30 hidden sm:flex items-center space-x-2 text-xs font-serif tracking-widest uppercase">
        <span>SINCE 1982</span>
        <span>✦</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        
        {/* Top Heritage Badge */}
        <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-[#E09F55]/15 border border-[#E09F55]/40 text-[#F2BC6F] text-xs font-bold uppercase tracking-[0.2em] mb-6 shadow-lg backdrop-blur-md">
          <Sparkles className="w-3.5 h-3.5 text-[#F2BC6F]" />
          <span>Taste South India’s Royal Heritage</span>
        </div>

        {/* Heading in Metallic Royal Gold */}
        <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-3xl mx-auto leading-[1.18] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#F7F4D6] via-[#F2BC6F] to-[#E09F55] drop-shadow-sm">
          Ready to Experience the Authentic Traditional Crunch?
        </h2>

        {/* Subtitle */}
        <p className="text-sm sm:text-base text-[#DDDAC4]/85 max-w-2xl mx-auto mt-4 font-sans leading-relaxed">
          Whether you’re ordering a 250g evening snack pack, custom wedding return gift boxes, corporate hampers, or bulk catering consignments, we handcraft every batch with generational care.
        </p>

        {/* 4 Action Cards with Royal Glassmorphism & Gold Borders */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-12 text-left font-sans">
          
          {/* 1. SHOP NOW */}
          <Link
            href="/shop"
            className="p-6 rounded-2xl bg-gradient-to-b from-[#260606]/90 via-[#190404]/80 to-[#100202]/95 hover:from-[#320808] hover:to-[#160303] border border-[#E09F55]/30 hover:border-[#F2BC6F] backdrop-blur-md transition-all duration-300 group flex flex-col justify-between space-y-4 shadow-xl hover:shadow-[0_12px_35px_rgba(224,159,85,0.25)] hover:-translate-y-1.5"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F2BC6F] to-[#E09F55] text-[#120303] flex items-center justify-center font-black group-hover:scale-110 transition-transform shadow-md">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h3 className="font-display font-semibold text-lg text-[#F7F4D6] group-hover:text-[#F2BC6F] transition-colors">
                Shop Snacks
              </h3>
              <p className="text-xs text-[#DDDAC4]/80 leading-relaxed">
                Explore our 24+ artisanal chips, murukku, mixture, and ghee sweets available for retail home ordering.
              </p>
            </div>
            <div className="inline-flex items-center space-x-1.5 text-xs font-bold text-[#F2BC6F] group-hover:translate-x-1.5 transition-transform">
              <span>Browse Catalog</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>

          {/* 2. BULK ORDER */}
          <Link
            href="/bulk-orders"
            className="p-6 rounded-2xl bg-gradient-to-b from-[#260606]/90 via-[#190404]/80 to-[#100202]/95 hover:from-[#320808] hover:to-[#160303] border border-[#E09F55]/30 hover:border-[#F2BC6F] backdrop-blur-md transition-all duration-300 group flex flex-col justify-between space-y-4 shadow-xl hover:shadow-[0_12px_35px_rgba(224,159,85,0.25)] hover:-translate-y-1.5"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#387613] to-[#1F5B18] text-white flex items-center justify-center font-black group-hover:scale-110 transition-transform shadow-md border border-[#E09F55]/40">
                <Truck className="w-6 h-6" />
              </div>
              <h3 className="font-display font-semibold text-lg text-[#F7F4D6] group-hover:text-[#F2BC6F] transition-colors">
                Bulk Orders
              </h3>
              <p className="text-xs text-[#DDDAC4]/80 leading-relaxed">
                Tier-volume wholesale pricing starting from 5kg to 1,000kg+ with airtight 5kg nitrogen storage packs.
              </p>
            </div>
            <div className="inline-flex items-center space-x-1.5 text-xs font-bold text-[#F2BC6F] group-hover:translate-x-1.5 transition-transform">
              <span>View Wholesale Tiers</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>

          {/* 3. CORPORATE */}
          <Link
            href="/corporate"
            className="p-6 rounded-2xl bg-gradient-to-b from-[#260606]/90 via-[#190404]/80 to-[#100202]/95 hover:from-[#320808] hover:to-[#160303] border border-[#E09F55]/30 hover:border-[#F2BC6F] backdrop-blur-md transition-all duration-300 group flex flex-col justify-between space-y-4 shadow-xl hover:shadow-[0_12px_35px_rgba(224,159,85,0.25)] hover:-translate-y-1.5"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E09F55] to-[#B8762D] text-[#120303] flex items-center justify-center font-black group-hover:scale-110 transition-transform shadow-md">
                <Building2 className="w-6 h-6" />
              </div>
              <h3 className="font-display font-semibold text-lg text-[#F7F4D6] group-hover:text-[#F2BC6F] transition-colors">
                Corporate Gifting
              </h3>
              <p className="text-xs text-[#DDDAC4]/80 leading-relaxed">
                Executive rigid hampers with metallic gold foil logo embossing, parchment greeting notes, and multi-branch dispatch.
              </p>
            </div>
            <div className="inline-flex items-center space-x-1.5 text-xs font-bold text-[#F2BC6F] group-hover:translate-x-1.5 transition-transform">
              <span>Corporate Proposals</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </Link>

          {/* 4. CONTACT */}
          <div
            onClick={() => setIsContactOpen(true)}
            className="p-6 rounded-2xl bg-gradient-to-b from-[#260606]/90 via-[#190404]/80 to-[#100202]/95 hover:from-[#320808] hover:to-[#160303] border border-[#E09F55]/30 hover:border-[#F2BC6F] backdrop-blur-md transition-all duration-300 group flex flex-col justify-between space-y-4 shadow-xl hover:shadow-[0_12px_35px_rgba(224,159,85,0.25)] hover:-translate-y-1.5 cursor-pointer"
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#950E09] to-[#6F0A06] border border-[#E09F55] text-white flex items-center justify-center font-black group-hover:scale-110 transition-transform shadow-md">
                <Phone className="w-6 h-6 text-[#F2BC6F]" />
              </div>
              <h3 className="font-display font-semibold text-lg text-[#F7F4D6] group-hover:text-[#F2BC6F] transition-colors">
                Contact Us
              </h3>
              <p className="text-xs text-[#DDDAC4]/80 leading-relaxed">
                Speak directly with our confectionery team or visit our flagship master kitchen on South Masi Street, Madurai.
              </p>
            </div>
            <div className="inline-flex items-center space-x-1.5 text-xs font-bold text-[#F2BC6F] group-hover:translate-x-1.5 transition-transform">
              <span>Open Contact Desk</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>

        </div>

        {/* Quick Direct WhatsApp Support Bar */}
        <div className="mt-12 inline-flex items-center space-x-2.5 px-6 py-2.5 rounded-full bg-[#180505]/90 border border-[#E09F55]/40 text-xs text-[#DDDAC4] shadow-xl backdrop-blur-md">
          <MessageCircle className="w-4 h-4 text-[#52A05B] animate-pulse" />
          <span>Instant Assistance on WhatsApp:</span>
          <a
            href="https://wa.me/919443210890"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-[#F2BC6F] hover:underline hover:text-white transition-colors"
          >
            +91 94432 10890
          </a>
        </div>

      </div>
    </section>
  );
}
